import React from 'react';
import ReactPlayer from 'react-player';
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { seconds, VideoProvider } from '~/interfaces';
import { VideoState } from '~/modules/Elements/VideoPlayer/VideoState';
import type { LoadVideByIdOpts, VideoPlayerRef } from './Youtube';
import type { VideoPlayerProps } from './index';

function clamp01(n: number) {
    return Math.min(1, Math.max(0, n));
}

function buildSrc(provider: VideoProvider, videoId: string) {
    switch (provider) {
        case 'dailymotion':
            return `https://www.dailymotion.com/video/${videoId}`;
        case 'vimeo':
            return `https://vimeo.com/${videoId}`;
        default:
            return videoId;
    }
}

type ReactPlayerApi = {
    seekTo: (amount: number, type?: 'seconds' | 'fraction') => void;
    getCurrentTime: () => number;
    getDuration: () => number;
};

const RP = ReactPlayer as unknown as React.ComponentType<Record<string, unknown>>;

export default forwardRef(function GenericVideoPlayer(
    { video, provider = 'dailymotion', autoplay, controls, volume, startAt, width, height, onStateChange }: VideoPlayerProps,
    ref: ForwardedRef<VideoPlayerRef>,
) {
    const rpApiRef = useRef<ReactPlayerApi | null>(null);

    const [status, setStatus] = useState<VideoState>(VideoState.UNSTARTED);
    const [playing, setPlaying] = useState<boolean>(Boolean(autoplay));
    const [playbackRate, setPlaybackRate] = useState<number>(1);
    const [forcedVolume, setForcedVolume] = useState<number | null>(null);

    const safeStartAt = useMemo(() => {
        const n = Number(startAt);
        return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
    }, [startAt]);

    const safeVolume = useMemo(() => {
        const v = forcedVolume ?? (volume ?? 0.5);
        return clamp01(v);
    }, [volume, forcedVolume]);

    const src = useMemo(() => buildSrc(provider ?? 'dailymotion', video), [provider, video]);

    useEffect(() => {
        setPlaying(Boolean(autoplay));
    }, [autoplay]);

    const startApplied = useRef(false);
    useEffect(() => {
        startApplied.current = false;
    }, [src]);

    useImperativeHandle(ref, () => ({
        getStatus: () => (status as unknown) as ReturnType<VideoPlayerRef['getStatus']>,
        setSize: () => { },

        seekTo: (timeSec: seconds) => {
            rpApiRef.current?.seekTo(timeSec, 'seconds');
        },

        setPlaybackSpeed: (speed: number) => {
            const s = Number(speed);
            if (Number.isFinite(s) && s > 0) setPlaybackRate(s);
        },

        setVolume: (newVolume: number) => {
            const n = Number(newVolume);
            if (!Number.isFinite(n)) return;
            const v = n > 1 ? n / 100 : n;
            setForcedVolume(clamp01(v));
        },

        getCurrentTime: async () => rpApiRef.current?.getCurrentTime?.() ?? 0,

        loadVideoById: (opts: LoadVideByIdOpts) => {
            rpApiRef.current?.seekTo(opts.startSeconds, 'seconds');
            setPlaying(true);
        },

        playVideo: () => setPlaying(true),
        pauseVideo: () => setPlaying(false),

        getDuration: async () => rpApiRef.current?.getDuration?.() ?? 0,
    }));

    return (
        <RP
            src={src}
            width={width}
            height={height}
            playing={playing}
            controls={Boolean(controls)}
            volume={safeVolume}
            playbackRate={playbackRate}
            onReady={(player: unknown) => {
                rpApiRef.current = player as ReactPlayerApi;

                setStatus(VideoState.CUED);
                onStateChange?.(VideoState.CUED);

                if (!startApplied.current && safeStartAt > 0) {
                    startApplied.current = true;
                    try {
                        rpApiRef.current?.seekTo(safeStartAt, 'seconds');
                    } catch { }
                }

                if (playing) {
                    setStatus(VideoState.PLAYING);
                    onStateChange?.(VideoState.PLAYING);
                }
            }}
            onPlay={() => {
                setStatus(VideoState.PLAYING);
                onStateChange?.(VideoState.PLAYING);
            }}
            onPause={() => {
                setStatus(VideoState.PAUSED);
                onStateChange?.(VideoState.PAUSED);
            }}
            onWaiting={() => {
                setStatus(VideoState.BUFFERING);
                onStateChange?.(VideoState.BUFFERING);
            }}
            onEnded={() => {
                setStatus(VideoState.ENDED);
                onStateChange?.(VideoState.ENDED);
            }}
            onError={(e: unknown) => {
                console.error('[GenericVideoPlayer] error', { provider, video, src, e });
            }}
        />
    );
});